const CardTypeRef = require('@/lib/PaymentMethods/CardTypeRef');
/**
 * Simplify the structure of response data for a successful payment or refund.
 * We want to store this as JSON in the payments.statusDetail field which can contain 1000 characters but a normal
 * payment is ~700 characters when minified. We want to make this quite a lot smaller to give ourselves headroom for
 * additional data while still leaving it human-readable (i.e. sensible key names not just an array)
 *
 * Example structure:
 *
 * {
 *     "id": "py_019233a6fedc7d4f88a89dd033ddf7f2",
 *     "amount": 2800,
 *     "amount_captured": 2800,
 *     "amount_refunded": 0,
 *     "amount_tip": 0,
 *     "created_at": "2024-09-27T13:23:55Z",
 *     "currency": "gbp",
 *     "description": "payment description",
 *     "device_id": "dv_0190d97b6fd879ccbefa46aed0b921cc",
 *     "last_error": null,
 *     "merchant_id": "mr_0190931e84d37273805d0d207d33768d",
 *     "metadata": null,
 *     "payment_method": {
 *         "card_present": {
 *             "brand": "visa",
 *             "last4": "9969",
 *             "read_method": null,
 *             "receipt": {
 *                 "application_preferred_name": "Stripe Credit",
 *                 "authorization_code": "123456",
 *                 "card_colder_verification_method": "approval",
 *                 "dedicated_file_name": "A000000003101001"
 *             }
 *         },
 *         "type": "card_present"
 *     },
 *     "reference": "1010172744223996401",
 *     "status": "succeeded",
 *     "updated_at": "2024-09-27T13:24:12Z"
 * }
 *
 * Simplified structure (approx 500 characters)
 *
 * {
 *     "id": "py_019233a6fedc7d4f88a89dd033ddf7f2",
 *     "amount": 2800,
 *     "captured": 2800,
 *     "refunded": 0,
 *     "tip": 0,
 *     "createdAt": "2024-09-27T13:23:55Z",
 *     "currency": "gbp",
 *     "deviceID": "dv_0190d97b6fd879ccbefa46aed0b921cc",
 *     "error": null,
 *     "merchantID": "mr_0190931e84d37273805d0d207d33768d",
 *     "metadata": null,
 *     "scheme": "visa",
 *     "last4": "9969",
 *     "method": null,
 *     "name": "Stripe Credit",
 *     "authCode": "123456",
 *     "verification": "approval",
 *     "aid": "A000000003101001",
 *     "type": "card_present",
 *     "ref": "1010172744223996401",
 *     "status": "succeeded",
 *     "updatedAt": "2024-09-27T13:24:12Z"
 * }
 *
 * @param {{}} data
 *
 * @return {object}
 */
const payyoTransactionParser = (data) => {
    let result = {
        id: data.id,
        refundId: null,
        amount: data.amount,
        captured: data.amount_captured,
        refunded: data.amount_refunded,
        tip: data?.amount_tip ?? 0,
        createdAt: data.created_at,
        refundedAt: null,
        currency: data.currency,
        deviceId: data.device_id,
        error: data.last_error,
        merchantId: data.merchant_id,
        metadata: data.metadata,
        scheme: data.payment_method?.card_present?.brand,
        cardTypeRef: convertCardBrandToCardTypeRef(data.payment_method?.card_present?.brand),
        last4: data.payment_method?.card_present?.last4 || data.payment_method?.card?.last4,
        method: data.payment_method?.card_present?.read_method,
        name: data.payment_method?.card_present?.receipt?.application_preferred_name,
        authCode: data.payment_method?.card_present?.receipt?.authorization_code,
        verification: data.payment_method?.card_present?.receipt?.card_holder_verification_method,
        aid: data.payment_method?.card_present?.receipt?.dedicated_file_name,
        type: data.payment_method?.type,
        ref: data.reference,
        status: data.status,
        updatedAt: data.updated_at,
    };
    if (data.isManualReconciliation) {
        result.isManualReconciliation = data.isManualReconciliation;
    }
    if (data.payment_method?.card) {
        result.method = 'Customer Not Present (CNP)';
    }
    return result;
};

/**
 * @param {string} brand
 *
 * @returns {string|null}
 */
const convertCardBrandToCardTypeRef = (brand) => {
    switch (brand) {
        case 'amex':
            return CardTypeRef.AMEX;
        case 'diners':
            return CardTypeRef.DINERS;
        case 'discover':
            return CardTypeRef.DISCOVER;
        case 'jcb':
            return CardTypeRef.JCB;
        case 'mastercard':
            return CardTypeRef.MASTERCARD;
        // case 'unionpay':
        //     return CardTypeRef.UNIONPAY;
        case 'visa':
            return CardTypeRef.VISA;
    }

    return null;
};

module.exports = payyoTransactionParser;
